import React, {useEffect, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActive } from '../store/DocumentStore';
import config from '../config';
import Menu from './Menu';
import './Documents.css';
import folderIcon from '../images/documents.png';

const Documents = () => {
    const dispatch = useDispatch();
    const documents = useSelector((state) => state.documentState.documents);
    const activeDocument = useSelector((state) => state.documentState.activeDocument); 

    const sortedDocs = React.useMemo(() =>
        documents
            ? documents.map(x => ({ ...x, category: x.category || '' }))
                .sort((a, b) => a.category.localeCompare(b.category))
            : [],
        [documents]
    );    
    const oldCategories = React.useMemo(() =>
        sortedDocs.map(item => item.category).filter((item, i, ar) => ar.indexOf(item) === i),
        [sortedDocs]
    );
    const [activeCat, setActiveCat] = useState((oldCategories && oldCategories[0]) || '');

    const categories = React.useMemo(() => convertToFolderStructure(oldCategories), [oldCategories]);

    const setActiveDocument = (doc) => {
        dispatch(setActive(doc));
    };

        // Clear active document when component unmounts (for fresh start on navigation)
    useEffect(() => {
        return () => {
            dispatch(setActive(null));
        };
    }, [dispatch]);

    // Only reset active document if current selection is invalid
    useEffect(() => {
        // Normalize category for comparison (remove spaces and uppercase)
        const normalizedActiveCat = activeCat.toUpperCase().replace(/\s+/g, '');
        
        // If active document exists and is in current category, keep it
        if (activeDocument && sortedDocs.some(d =>
            d.id === activeDocument.id &&
            d.category.toUpperCase().replace(/\s+/g, '') === normalizedActiveCat
        )) {
            return; // Don't reset - user's choice is valid
        }

        // Otherwise set default (first load, category changed, or doc was deleted)
        const filtered = sortedDocs.filter(x => 
            x.category.toUpperCase().replace(/\s+/g, '') === normalizedActiveCat
        );
        if (filtered?.length > 0) {
            setActiveDocument(filtered[0]);
        } else if (sortedDocs.length > 0) {
            setActiveDocument(sortedDocs[0]);
        }
    }, [activeCat, sortedDocs, activeDocument, dispatch]);

    return (
        <div className="App">
            <Menu />
            <div className="documents">
                <div className='documents-container'>
                    {categories && categories.map(c =>
                        renderCategory(c, activeCat, setActiveCat, sortedDocs, activeDocument, setActiveDocument)
                    )}
                </div>
                {activeDocument &&
                    <div className='preview'>
                        <embed key={`${config.server}${activeDocument.path}`} src={`${config.server}${activeDocument.path}`} alt="pdf" pluginspage="http://www.adobe.com/products/acrobat/readstep2.html" />
                    </div>
                }
            </div>
        </div>
    );
};

function renderCategory(category, activeCat, setActiveCat, sortedDocs, activeDocument, setActiveDocument, path, depth = 0) {    const fullPath = path ? `${path}|${category.name}` : category.name;
    const normalizedPath = fullPath.replace(/\s+/g, '');
    const indentClass = `category-item depth-${depth}`;

    const toggleCategory = () => {
        setActiveCat(normalizedPath === activeCat ? '' : normalizedPath);
    };

    const isMatchingCategory = (documentCategory) => {
        return documentCategory.toUpperCase().replace(/\s+/g, '') === normalizedPath;
    };

    return (
        <div key={category.name} className={indentClass}>
            <h2 className={normalizedPath === activeCat ? 'expanded' : ''} onClick={toggleCategory}>
                <img src={folderIcon} alt={'folder'} />
                {`${category.name || 'Ingen kategori'}`}
                <span className={'expand'}>{normalizedPath === activeCat ? '-' : '+'}</span>
            </h2>
            {normalizedPath === activeCat && sortedDocs && sortedDocs.filter(x => isMatchingCategory(x.category)).map((d) => {
                return (
                    <div key={d.id} onClick={() => setActiveDocument(d)} className={`document ${activeDocument && d.id === activeDocument.id ? 'active' : ''}`}>
                        {d.name}
                    </div>
                );
            })}
            {category.subcategories && category.subcategories.map(subCat => renderCategory(subCat, activeCat, setActiveCat, sortedDocs, activeDocument, setActiveDocument, fullPath, depth + 1))}
        </div>
    );
}

function convertToFolderStructure(categories) {
    const categoryMap = {};

    for (const category of categories) {
        const categoryParts = category.split('|').map(part => part.trim().toUpperCase()); 
        let currentCategoryMap = categoryMap;

        for (const part of categoryParts) {
            if (!currentCategoryMap[part]) {
                currentCategoryMap[part] = {};
            }
            currentCategoryMap = currentCategoryMap[part];
        }
    }

    function convertMapToStructure(map) {
        const folderStructure = [];

        for (const key in map) {
            const subfolder = {
                name: key,
                subcategories: convertMapToStructure(map[key]),
            };
            folderStructure.push(subfolder);
        }

        return folderStructure;
    }

    return convertMapToStructure(categoryMap);
}

export default Documents;